/* ============================================================
   V1 — Licensing Navigator
   Three certification paths, phase by phase, with progress persisted.
   ============================================================ */

import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { session } from '../../shared/storage.js';
import { PrintButton } from '../../components/PrintButton.js';
import { setPageMeasure } from '../../shared/measure.js';

const PATHS = {
  'road-freight': {
    name: 'Road Freight Operator',
    authority: 'NTSA, KRA, county government',
    duration: 'Typically 8–14 weeks',
    phases: [
      {
        name: 'Entity & tax',
        steps: [
          'Register the company with the Business Registration Service',
          'Obtain a KRA PIN and register for VAT where turnover requires it',
          'Open a corporate bank account and register for eCitizen services',
          'Obtain a county single business permit for each operating location'
        ]
      },
      {
        name: 'Operator licensing',
        steps: [
          'Apply for the NTSA Commercial Vehicle Operator Licence',
          'Nominate a safety officer and document the safety management system',
          'Register each vehicle and obtain a Commercial Vehicle Road Licence',
          'Fit NTSA-compliant telematics across the fleet'
        ]
      },
      {
        name: 'Insurance & compliance',
        steps: [
          'Place comprehensive fleet, goods-in-transit and public liability cover',
          'Verify driver licence classes and medical fitness certificates',
          'Establish driver hours logging and the fatal-accident reporting protocol',
          'Register with the Kenya Transporters Association if operating transit cargo'
        ]
      },
      {
        name: 'Operational readiness',
        steps: [
          'Set up maintenance records and a preventive service schedule',
          'Establish fuel procurement and reconciliation controls',
          'Document standard rates by lane using a cost-per-ton-km model',
          'Run a pilot lane before committing to contracted volume'
        ]
      }
    ]
  },

  'customs-agent': {
    name: 'Customs Clearing Agent',
    authority: 'KRA',
    duration: 'Typically 10–16 weeks',
    phases: [
      {
        name: 'Entity & eligibility',
        steps: [
          'Register the company and obtain a KRA PIN',
          'Confirm directors meet KRA fit-and-proper requirements',
          'Secure premises with the record-keeping capacity KRA expects',
          'Obtain a county single business permit'
        ]
      },
      {
        name: 'Professional qualification',
        steps: [
          'Ensure at least one director or employee holds the required customs qualification',
          'Complete the East African Customs and Freight Forwarding Practising Certificate',
          'Register the qualified person against the licence application'
        ]
      },
      {
        name: 'Licence application',
        steps: [
          'Lodge the customs agent licence application with KRA',
          'Provide the security bond required for the licence category',
          'Obtain iCMS user credentials for each declarant',
          'Complete KRA system training and sandbox declarations'
        ]
      },
      {
        name: 'Operations',
        steps: [
          'Register with KIFWA and observe its code of conduct',
          'Establish a client duty-payment escrow discipline',
          'Build an HS classification reference mapped to KRA reference values',
          'Set up an annual licence renewal reminder'
        ]
      }
    ]
  },

  'bonded-warehouse': {
    name: 'Bonded Warehouse Operator',
    authority: 'KRA, county government, NEMA',
    duration: 'Typically 16–28 weeks',
    phases: [
      {
        name: 'Site & entity',
        steps: [
          'Register the company and obtain a KRA PIN',
          'Secure a site with suitable access, hardstanding and perimeter',
          'Obtain change-of-use and construction approvals from the county',
          'Complete a NEMA environmental impact assessment where required'
        ]
      },
      {
        name: 'Facility build-out',
        steps: [
          'Install perimeter security, access control and CCTV to KRA standard',
          'Provide a segregated customs office and examination area',
          'Install a warehouse management system capable of KRA stock returns',
          'Complete fire safety certification and occupational safety registration'
        ]
      },
      {
        name: 'Licensing',
        steps: [
          'Lodge the bonded warehouse licence application with KRA',
          'Execute the CB6 security bond sized against peak duty exposure',
          'Pass the KRA pre-licensing site inspection',
          'Obtain iCMS warehouse module access'
        ]
      },
      {
        name: 'Going live',
        steps: [
          'Establish monthly stock return procedures',
          'Set the reconciliation discipline between physical and system stock',
          'Place warehouse liability and stock cover',
          'Schedule the annual licence renewal and bond review'
        ]
      }
    ]
  }
};

const STORAGE_KEY = 'licensing_progress';

export function LicensingPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Licensing Navigator</h1>
      <p>Three certification paths, broken into phases. Your ticks are saved on this device.</p>
    </header>
    <div id="licensing-body"></div>
  `;

  renderSelector(page.querySelector('#licensing-body'));
  return page;
}

function renderSelector(container) {
  setPageMeasure(container, 'wide');
  container.replaceChildren();
  const list = document.createElement('div');
  /* tool-grid, not stack: these are three or four sibling choices, and
     stacking them put one 640px-wide card per row with 740px of empty
     page beside it. The same cards as an auto-fill grid are one row. */
  list.className = 'tool-grid';

  for (const [key, path] of Object.entries(PATHS)) {
    const done = countDone(key, path);
    const total = countSteps(path);

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'card card-link';
    card.innerHTML = html`
      <div class="card-stack">
      <h3 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-1);">${path.name}</h3>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-3);">
        ${path.authority} · ${path.duration}
      </p>
      <div class="progress-track">
        <div class="progress-fill" style="width: ${total ? (done / total) * 100 : 0}%;"></div>
      </div>
      <div class="num" style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-2);">
        ${done} of ${total} steps complete
      </div>
      </div>
    `;
    card.addEventListener('click', () => renderDetail(container, key));
    list.appendChild(card);
  }

  container.appendChild(list);
}

function renderDetail(container, key) {
  setPageMeasure(container, 'reading');
  const path = PATHS[key];
  const progress = session.get(`${STORAGE_KEY}_${key}`, {}) || {};

  container.replaceChildren();

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn-ghost no-print';
  back.style.marginBottom = 'var(--ca-space-4)';
  back.innerHTML = `${icon('left', 16)}<span>All paths</span>`;
  back.addEventListener('click', () => renderSelector(container));
  container.appendChild(back);

  const body = document.createElement('div');
  body.id = 'licensing-detail';
  container.appendChild(body);

  // Rebuilt on every tick; carry keyboard focus through the repaint.
  const paint = (focusId = null) => {
    const done = countDone(key, path);
    const total = countSteps(path);

    body.replaceChildren();
    body.innerHTML = html`
      <h2 style="font-size: var(--ca-text-lg); margin-bottom: var(--ca-space-1);">${path.name}</h2>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-4);">
        ${path.authority} · ${path.duration}
      </p>
      <div class="progress" style="margin-bottom: var(--ca-space-6);">
        <div class="progress-head">
          <span>Overall progress</span>
          <span class="num">${done} / ${total}</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" style="width: ${total ? (done / total) * 100 : 0}%;"></div>
        </div>
      </div>
    `;

    path.phases.forEach((phase, phaseIndex) => {
      const section = document.createElement('section');
      section.className = 'card avoid-break';
      section.style.marginBottom = 'var(--ca-space-3)';

      const phaseDone = phase.steps.filter((_, i) => progress[`${phaseIndex}-${i}`]).length;

      section.innerHTML = html`
        <div class="row" style="margin-bottom: var(--ca-space-3);">
          <h3 style="flex:1; font-size: var(--ca-text-sm);">
            Phase ${phaseIndex + 1} · ${phase.name}
          </h3>
          <span class="badge num" style="background: var(--ca-surface-sunk); color: var(--ca-text-tertiary);">
            ${phaseDone}/${phase.steps.length}
          </span>
        </div>
        <div class="stack" style="gap: var(--ca-space-2);" data-steps></div>
      `;

      const steps = section.querySelector('[data-steps]');
      phase.steps.forEach((step, stepIndex) => {
        const id = `${phaseIndex}-${stepIndex}`;
        const checked = Boolean(progress[id]);

        const label = document.createElement('label');
        label.className = 'row';
        label.dataset.step = id;
        label.style.cssText =
          'align-items: flex-start; gap: var(--ca-space-3); cursor: pointer; padding: var(--ca-space-1) 0;';
        label.innerHTML = html`
          <input type="checkbox" ${checked ? 'checked' : ''} style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent);" />
          <span style="flex:1; font-size: var(--ca-text-sm); color: ${checked ? 'var(--ca-text-quaternary)' : 'var(--ca-text-secondary)'};
                       text-decoration: ${checked ? 'line-through' : 'none'};">
            ${step}
          </span>
        `;

        label.querySelector('input').addEventListener('change', (event) => {
          if (event.target.checked) progress[id] = true;
          else delete progress[id];
          session.set(`${STORAGE_KEY}_${key}`, progress);
          paint(id);
        });

        steps.appendChild(label);
      });

      body.appendChild(section);
    });

    body.appendChild(
      PrintButton({
        targetId: 'licensing-detail',
        title: `${path.name} — Licensing Checklist`,
        subtitle: `${path.authority} · ${done} of ${total} steps complete`
      })
    );

    if (focusId) {
      const restored = body.querySelector(`[data-step="${CSS.escape(focusId)}"] input`);
      if (restored) restored.focus({ preventScroll: true });
    }
  };

  paint();
}

function countSteps(path) {
  return path.phases.reduce((sum, phase) => sum + phase.steps.length, 0);
}

function countDone(key, path) {
  const progress = session.get(`${STORAGE_KEY}_${key}`, {}) || {};
  let done = 0;
  path.phases.forEach((phase, phaseIndex) => {
    phase.steps.forEach((_, stepIndex) => {
      if (progress[`${phaseIndex}-${stepIndex}`]) done++;
    });
  });
  return done;
}
